import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { 
    ArrowPathIcon, 
    PlayIcon, 
    EyeIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    ClockIcon,
    ChartBarIcon
} from '@heroicons/react/24/outline';

function TrackingSyncPanel() {
    const { token } = useAuth();
    const [analysis, setAnalysis] = useState(null);
    const [syncResults, setSyncResults] = useState(null);
    const [syncHistory, setSyncHistory] = useState(null);
    const [comparison, setComparison] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('analysis');

    // Analiz yap
    // Senkronizasyon geçmişini yükle
    const loadSyncHistory = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/smart-tracking/sync-history', {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            console.log('[TrackingSyncPanel] Senkronizasyon geçmişi API yanıtı:', response);
            
            if (response.success && response.data) {
                setSyncHistory(response);
            } else {
                console.error('[TrackingSyncPanel] Senkronizasyon geçmişi verisi eksik:', response);
                setSyncHistory({
                    success: true,
                    data: {
                        logs: [],
                        stats: {
                            totalLogs: 0,
                            completedLogs: 0,
                            activeLogs: 0,
                            errorLogs: 0,
                            todayLogs: 0
                        }
                    }
                });
            }
        } catch (error) {
            console.error('[TrackingSyncPanel] Senkronizasyon geçmişi hatası:', error);
            toast.error('Senkronizasyon geçmişi yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const runAnalysis = async () => {
        setLoading(true);
        try {
            const response = await api.get('/api/smart-tracking/analyze', {
                headers: { Authorization: `Bearer ${token}` }
            });

            console.log('[TrackingSyncPanel] Analiz API yanıtı:', response);
            
            if (response.success && response.data) {
                setAnalysis(response);
                toast.success('Analiz tamamlandı');
            } else {
                console.error('[TrackingSyncPanel] Analiz verisi eksik:', response);
                setAnalysis({
                    success: true,
                    data: {
                        totalOrdersWithKargonomi: 0,
                        ordersWithoutTracking: 0,
                        ordersWithTracking: 0,
                        activeTrackingLogs: 0,
                        needsSync: 0,
                        orders: []
                    }
                });
            }
        } catch (error) {
            toast.error('Analiz hatası: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Otomatik senkronizasyon başlat
    const startAutoSync = async () => {
        if (!window.confirm('Otomatik senkronizasyon başlatılsın mı? Bu işlem birkaç dakika sürebilir.')) {
            return;
        }

        setLoading(true);
        try {
            const response = await api.post('/api/smart-tracking/auto-sync', {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.success) {
                setSyncResults(response.data);
                toast.success(response.message);
                // Analizi yenile
                await runAnalysis();
            }
        } catch (error) {
            toast.error('Senkronizasyon hatası: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Kargonomi karşılaştırması
    const compareWithKargonomi = async () => {
        setLoading(true);
        try {
            const response = await api.post('/api/smart-tracking/compare-with-kargonomi', {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            console.log('[TrackingSyncPanel] Karşılaştırma API yanıtı:', response);
            
            if (response.success && response.data) {
                setComparison(response);
                toast.success('Karşılaştırma tamamlandı');
            } else {
                console.error('[TrackingSyncPanel] Karşılaştırma verisi eksik:', response);
                setComparison({
                    success: true,
                    data: {
                        comparisons: [],
                        total: 0,
                        needsUpdate: 0
                    }
                });
            }
        } catch (error) {
            toast.error('Karşılaştırma hatası: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Manuel takip başlat
    const startManualTracking = async (orderId) => {
        try {
            const response = await api.post(`/api/smart-tracking/start/${orderId}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.success) {
                setSyncResults(response); // ✅ Senkronizasyon sonuçlarını kaydet
                toast.success(response.message);
                // Senkronizasyon tamamlandı, analiz sekmesinde kal
                await runAnalysis(); // Analizi yenile
            }
        } catch (error) {
            toast.error('Takip başlatma hatası: ' + error.message);
        }
    };

    useEffect(() => {
        runAnalysis();
    }, []);

    const getStatusColor = (status) => {
        switch (status) {
            case 'success': return 'text-green-600 bg-green-100';
            case 'error': return 'text-red-600 bg-red-100';
            case 'skipped': return 'text-yellow-600 bg-yellow-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                            <ArrowPathIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Takip Senkronizasyonu</h2>
                            <p className="text-gray-600">Kargonomi ve veritabanı arasında senkronizasyon</p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-3">
                        {/* Tab Navigation */}
                        <div className="flex bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => setActiveTab('analysis')}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                                    activeTab === 'analysis'
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                <ChartBarIcon className="w-4 h-4 mr-2 inline" />
                                Analiz
                            </button>
                            <button
                                onClick={() => setActiveTab('compare')}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                                    activeTab === 'compare'
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                <EyeIcon className="w-4 h-4 mr-2 inline" />
                                Karşılaştır
                            </button>
                        </div>
                        
                        <button 
                            onClick={runAnalysis} 
                            disabled={loading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                        >
                            <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                            {loading ? 'Analiz Ediliyor...' : 'Analiz Et'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Analysis Tab */}
            {activeTab === 'analysis' && analysis && (
                <div className="space-y-6">
                    {/* İstatistikler */}
                    <div className="bg-white rounded-2xl shadow-xl p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Senkronizasyon Durumu</h3>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                <p className="text-sm font-medium text-blue-600">Toplam Kargonomi Siparişi</p>
                                <p className="text-2xl font-bold text-blue-700">{analysis?.data?.totalOrdersWithKargonomi || 0}</p>
                            </div>
                            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                                <p className="text-sm font-medium text-red-600">Takipsiz Siparişler</p>
                                <p className="text-2xl font-bold text-red-700">{analysis?.data?.ordersWithoutTracking || 0}</p>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                <p className="text-sm font-medium text-green-600">Takipli Siparişler</p>
                                <p className="text-2xl font-bold text-green-700">{analysis?.data?.ordersWithTracking || 0}</p>
                            </div>
                            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                                <p className="text-sm font-medium text-yellow-600">Senkronizasyon Gerekli</p>
                                <p className="text-2xl font-bold text-yellow-700">{analysis?.data?.needsSync || 0}</p>
                            </div>
                        </div>
                    </div>

                    {/* Senkronizasyon Gerekli Siparişler */}
                    {analysis?.data?.orders && analysis.data.orders.length > 0 && (
                        <div className="bg-white rounded-2xl shadow-xl p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Son Kargonomi Siparişleri</h3>
                                <button
                                    onClick={startAutoSync}
                                    disabled={loading || (analysis?.data?.needsSync || 0) === 0}
                                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center disabled:opacity-50"
                                >
                                    <PlayIcon className="h-4 w-4 mr-2" />
                                    Otomatik Senkronizasyon Başlat
                                </button>
                            </div>
                            <div className="space-y-3 max-h-96 overflow-y-auto">
                                {(analysis?.data?.orders || []).map(order => (
                                    <div key={order.id} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-4">
                                                <span className="font-mono text-sm font-medium text-gray-900">{order.order_number}</span>
                                                <span className="text-sm text-gray-600">Kargonomi ID: {order.kargonomi_shipment_id}</span>
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                                    order.tracking_status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {order.tracking_status || 'Takipsiz'}
                                                </span>
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                                    order.status === 'shipped' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {order.status}
                                                </span>
                                            </div>
                                            {!order.tracking_status && ['shipped', 'confirmed'].includes(order.status) && (
                                                <button
                                                    onClick={() => startManualTracking(order.id)}
                                                    className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                                                >
                                                    Takip Başlat
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Sync Results Tab */}
            {activeTab === 'sync' && syncResults && (
                <div className="bg-white rounded-2xl shadow-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Senkronizasyon Sonuçları</h3>
                    
                    {/* Özet */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-sm font-medium text-gray-600">Toplam</p>
                            <p className="text-2xl font-bold text-gray-700">{syncResults?.data?.results?.length || 0}</p>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg">
                            <p className="text-sm font-medium text-green-600">Başarılı</p>
                            <p className="text-2xl font-bold text-green-700">{syncResults?.data?.results?.filter(r => r.success)?.length || 0}</p>
                        </div>
                        <div className="bg-red-50 p-4 rounded-lg">
                            <p className="text-sm font-medium text-red-600">Hata</p>
                            <p className="text-2xl font-bold text-red-700">{syncResults?.data?.results?.filter(r => !r.success)?.length || 0}</p>
                        </div>
                    </div>

                    {/* Detaylar */}
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                        {(syncResults?.data?.results || []).map((result, index) => (
                            <div key={index} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                        <span className="font-mono text-sm font-medium text-gray-900">{result.orderNumber}</span>
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(result.status)}`}>
                                            {result.status}
                                        </span>
                                        <span className="text-sm text-gray-600">{result.message}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Compare Tab */}
            {activeTab === 'compare' && (
                <div className="bg-white rounded-2xl shadow-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Kargonomi Karşılaştırması</h3>
                        <button
                            onClick={compareWithKargonomi}
                            disabled={loading}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
                        >
                            <EyeIcon className="h-4 w-4 mr-2" />
                            Karşılaştır
                        </button>
                    </div>

                    {comparison && (
                        <>
                            {/* Özet */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-sm font-medium text-gray-600">Toplam Karşılaştırma</p>
                                    <p className="text-2xl font-bold text-gray-700">{comparison?.data?.comparisons?.length || 0}</p>
                                </div>
                                <div className="bg-yellow-50 p-4 rounded-lg">
                                    <p className="text-sm font-medium text-yellow-600">Güncelleme Gerekli</p>
                                    <p className="text-2xl font-bold text-yellow-700">{comparison?.data?.comparisons?.filter(c => c.needsUpdate)?.length || 0}</p>
                                </div>
                                <div className="bg-red-50 p-4 rounded-lg">
                                    <p className="text-sm font-medium text-red-600">Hata</p>
                                    <p className="text-2xl font-bold text-red-700">{comparison?.data?.comparisons?.filter(c => c.error)?.length || 0}</p>
                                </div>
                            </div>

                            {/* Karşılaştırma Detayları */}
                            <div className="space-y-4 max-h-96 overflow-y-auto">
                                {comparison.data.comparisons.map((comp, index) => (
                                    <div key={index} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-mono text-sm font-medium text-gray-900">{comp.orderNumber}</span>
                                            {comp.needsUpdate && (
                                                <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                    Güncelleme Gerekli
                                                </span>
                                            )}
                                            {comp.error && (
                                                <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                    Hata
                                                </span>
                                            )}
                                        </div>
                                        
                                        {comp.database && comp.kargonomi && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                                <div>
                                                    <p className="font-medium text-gray-700">Veritabanı:</p>
                                                    <p>Takip No: {comp.database.trackingNumber || 'Yok'}</p>
                                                    <p>Durum: {comp.database.trackingStatus || 'Yok'}</p>
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-700">Kargonomi:</p>
                                                    <p>Durum: {comp.kargonomi.statusLabel}</p>
                                                    <p>Referans: {comp.kargonomi.referenceNumber || 'Yok'}</p>
                                                    <p>Takip: {comp.kargonomi.trackingCode || 'Yok'}</p>
                                                    <p>Firma: {comp.kargonomi.shippingProvider}</p>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {comp.error && (
                                            <p className="text-red-600 text-sm mt-2">{comp.error}</p>
                                        )}
                                        
                                        {comp.updateReason && (
                                            <p className="text-yellow-600 text-sm mt-2">{comp.updateReason}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default TrackingSyncPanel;